import React, { memo } from 'react';

export interface LoadingStateProps {
  loading?: boolean;
  error?: string | Error | null;
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
  size?: 'small' | 'medium' | 'large';
  overlay?: boolean;
  delay?: number;
  onRetry?: () => void;
  className?: string;
}

const sizeClasses = {
  small: 'p-4',
  medium: 'p-8',
  large: 'p-12'
};

const spinnerSizeClasses = {
  small: 'w-6 h-6 border-2',
  medium: 'w-10 h-10 border-[3px]',
  large: 'w-[60px] h-[60px] border-4'
};

const textSizeClasses = {
  small: 'text-sm',
  medium: 'text-base',
  large: 'text-lg'
};

export const LoadingState = memo(function LoadingState({
  loading = false,
  error = null,
  children,
  loadingComponent,
  errorComponent,
  size = 'medium',
  overlay = false,
  delay = 0,
  onRetry,
  className = ''
}: LoadingStateProps) {
  const [showLoading, setShowLoading] = React.useState(!delay);

  React.useEffect(() => {
    if (loading && delay > 0) {
      const timer = setTimeout(() => setShowLoading(true), delay);
      return () => clearTimeout(timer);
    } else {
      setShowLoading(loading);
    }
  }, [loading, delay]);

  const errorMessage = error instanceof Error ? error.message : error;

  if (error) {
    if (errorComponent) {
      return <>{errorComponent}</>;
    }

    return (
      <div className={`flex items-center justify-center ${sizeClasses[size]} bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-lg ${className}`}>
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="text-3xl text-danger-500">⚠️</div>
          <div className={`text-neutral-500 dark:text-neutral-400 ${textSizeClasses[size]} max-w-[400px] leading-relaxed`}>{errorMessage}</div>
          {onRetry && (
            <button
              onClick={onRetry}
              className="bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white border-none px-4 py-2 rounded text-sm cursor-pointer transition-colors duration-200"
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  if (loading && showLoading) {
    if (loadingComponent) {
      return overlay ? (
        <div className={`relative ${className}`}>
          {children}
          <div className="absolute inset-0 bg-white/80 dark:bg-neutral-900/80 flex items-center justify-center z-10 backdrop-blur-sm">
            {loadingComponent}
          </div>
        </div>
      ) : (
        <>{loadingComponent}</>
      );
    }

    const loadingContent = (
      <div className={`flex items-center justify-center ${sizeClasses[size]} ${className}`}>
        <div className="flex flex-col items-center gap-4">
          <div className={`${spinnerSizeClasses[size]} border-neutral-200 dark:border-neutral-700 border-t-primary-500 dark:border-t-primary-400 rounded-full animate-spin`} />
          <div className={`text-neutral-500 dark:text-neutral-400 ${textSizeClasses[size]} font-medium`}>Loading...</div>
        </div>
      </div>
    );

    if (overlay) {
      return (
        <div className={`relative ${className}`}>
          {children}
          <div className="absolute inset-0 bg-white/80 dark:bg-neutral-900/80 flex items-center justify-center z-10 backdrop-blur-sm">
            {loadingContent}
          </div>
        </div>
      );
    }

    return loadingContent;
  }

  return <>{children}</>;
});

// Skeleton loading component for more specific loading states
export interface SkeletonProps {
  width?: string | number;
  height?: string | number;
  borderRadius?: string | number;
  className?: string;
  animate?: boolean;
}

export const Skeleton = memo(function Skeleton({
  width = '100%',
  height = '1rem',
  borderRadius = '4px',
  className = '',
  animate = true
}: SkeletonProps) {
  return (
    <div
      className={`bg-neutral-200 dark:bg-neutral-700 ${animate ? 'animate-pulse' : ''} ${className}`}
      style={{
        width,
        height,
        borderRadius
      }}
    />
  );
});

// Table skeleton for loading tables
export interface TableSkeletonProps {
  rows?: number;
  columns?: number;
  showHeader?: boolean;
  className?: string;
}

export const TableSkeleton = memo(function TableSkeleton({
  rows = 5,
  columns = 4,
  showHeader = true,
  className = ''
}: TableSkeletonProps) {
  return (
    <div className={`w-full border border-neutral-200 dark:border-neutral-700 rounded-lg overflow-hidden ${className}`}>
      {showHeader && (
        <div className="grid gap-4 p-4 bg-neutral-50 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700" style={{ gridTemplateColumns: `repeat(${columns}, minmax(150px, 1fr))` }}>
          {Array.from({ length: columns }, (_, i) => (
            <Skeleton key={i} height="2rem" />
          ))}
        </div>
      )}
      <div className="p-4">
        {Array.from({ length: rows }, (_, rowIndex) => (
          <div key={rowIndex} className={`grid gap-4 ${rowIndex < rows - 1 ? 'mb-4' : ''}`} style={{ gridTemplateColumns: `repeat(${columns}, minmax(150px, 1fr))` }}>
            {Array.from({ length: columns }, (_, colIndex) => (
              <Skeleton key={colIndex} height="1.5rem" />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
});

// Chart skeleton for loading charts
export interface ChartSkeletonProps {
  type?: 'bar' | 'line' | 'pie';
  width?: string | number;
  height?: string | number;
  className?: string;
}

export const ChartSkeleton = memo(function ChartSkeleton({
  type = 'bar',
  width = '100%',
  height = '300px',
  className = ''
}: ChartSkeletonProps) {
  return (
    <div
      className={`border border-neutral-200 dark:border-neutral-700 rounded-lg p-4 bg-neutral-50 dark:bg-neutral-800 ${className}`}
      style={{ width, height }}
    >
      <div className="w-full h-full flex items-end justify-center">
        {type === 'bar' && (
          <div className="flex items-end gap-4 h-full w-full justify-around">
            {Array.from({ length: 6 }, (_, i) => (
              <Skeleton
                key={i}
                width="40px"
                height={`${Math.random() * 60 + 40}%`}
                borderRadius="2px"
              />
            ))}
          </div>
        )}
        {type === 'line' && (
          <div className="w-full h-full flex items-center">
            <Skeleton height="2px" borderRadius="1px" />
          </div>
        )}
        {type === 'pie' && (
          <div className="flex items-center justify-center h-full">
            <Skeleton width="200px" height="200px" borderRadius="50%" />
          </div>
        )}
      </div>
    </div>
  );
});